import { z } from "zod";
// This is the only place where we actually want to have this import
// oxlint-disable-next-line no-restricted-imports
import { format as defaultFormat, parse } from "date-fns";
import { sv } from "date-fns/locale";
import type { ITimeZone } from "./organizations";

type CommonDateFormat = "yyyy-MM-dd";
type NonCreatedOccurrenceDateFormat = "yyyyMMdd";
type CommonTimeFormat = "HH:mm";

// For human-facing datetime formatting.
// Recreated signature from deprecated humanFormat() from @lingui/react/macro for any human-facing output.
export const humanFormat = (
  value?: Parameters<Intl.DateTimeFormat["format"]>[0],
  format?: Intl.DateTimeFormatOptions,
): string =>
  new Intl.DateTimeFormat(
    // The runtime's default locale is used when undefined is passed
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#locales
    undefined,
    format,
  ).format(value);

// For machine-readable date formatting only (e.g. API params, keys).
export const machineFormat = (
  date: Parameters<typeof defaultFormat>[0],
  formatStr:
    | CommonDateFormat
    | CommonTimeFormat
    | NonCreatedOccurrenceDateFormat,
  options?: Parameters<typeof defaultFormat>[2],
): ReturnType<typeof defaultFormat> =>
  defaultFormat(date, formatStr, {
    ...options,
    locale: sv,
  });

const _dateTimeSchema = z.iso.datetime();
export type IDateTime = z.infer<typeof _dateTimeSchema>;

// A codec, not a validator: the wire carries an ISO 8601 string with an offset,
// the frontend works with a Date. Declaring it this way keeps the two sides
// separable — `z.toJSONSchema(schema, { io: "input" })` yields the wire shape a
// backend has to satisfy, and `z.encode` produces that shape again on the way out.
// The input side is deliberately string-only. Schemas that describe already
// decoded objects (the patient-hydrated ones) are type declarations, not parsers.
export const dateSchema = z.codec(z.iso.datetime({ offset: true }), z.date(), {
  decode: (isoDateAsString) => new Date(isoDateAsString),
  encode: (date) => date.toISOString(),
});

export const timestampSchema = dateSchema.nullish();

// A calendar date without a time of day, spelled `yyyy-MM-dd` on the wire and held as
// midnight in the device's timezone. Encoding formats in the device's timezone too, so a
// picked date is sent as the same calendar day regardless of its UTC offset. Calendar dates
// are the organization's days, so this relies on the device sharing the organization's
// timezone.
export const calendarDateSchema = z.codec(z.iso.date(), z.date(), {
  decode: (isoDate) => parse(isoDate, "yyyy-MM-dd", new Date()),
  encode: (date) => machineFormat(date, "yyyy-MM-dd"),
});

// The value of an `<input type="date">`, which reports a cleared field as "". Decoding at
// the form boundary leaves nothing downstream able to mistake "" for a date.
export const dateInputSchema = z.codec(
  z.union([z.literal(""), z.iso.date()]),
  z.date().nullable(),
  {
    decode: (value) => (value === "" ? null : calendarDateSchema.decode(value)),
    encode: (date) => (date === null ? "" : calendarDateSchema.encode(date)),
  },
);

// The clocks of a time zone read at an instant, independent of the device's own time zone.
const wallClockInTimeZone = ({
  instant,
  timeZone,
}: {
  instant: Date;
  timeZone: ITimeZone;
}) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((candidate) => candidate.type === type)?.value);
  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: part("hour"),
    minute: part("minute"),
    second: part("second"),
  };
};

const twoDigits = (value: number) => String(value).padStart(2, "0");

// The calendar date, yyyy-MM-dd, that an instant falls on in the time zone.
export const toPlainDateInTimeZone = ({
  instant,
  timeZone,
}: {
  instant: Date;
  timeZone: ITimeZone;
}) => {
  const { year, month, day } = wallClockInTimeZone({ instant, timeZone });
  return `${year}-${twoDigits(month)}-${twoDigits(day)}`;
};

// The time of day, HH:mm, that the time zone's clocks show at an instant.
export const toPlainTimeInTimeZone = ({
  instant,
  timeZone,
}: {
  instant: Date;
  timeZone: ITimeZone;
}) => {
  const { hour, minute } = wallClockInTimeZone({ instant, timeZone });
  return `${twoDigits(hour)}:${twoDigits(minute)}`;
};

// The time zone's offset from UTC at an instant, in milliseconds.
const offsetInTimeZone = ({
  instant,
  timeZone,
}: {
  instant: Date;
  timeZone: ITimeZone;
}) => {
  const { year, month, day, hour, minute, second } = wallClockInTimeZone({
    instant,
    timeZone,
  });
  const wholeSeconds = Math.floor(instant.getTime() / 1000) * 1000;
  return Date.UTC(year, month - 1, day, hour, minute, second) - wholeSeconds;
};

// The last instant of a calendar date, yyyy-MM-dd, in the time zone. The offset is taken at a
// first estimate and taken again at the result, which is exact for any wall-clock time that
// occurs exactly once; end of day is never inside a daylight saving transition in the zones
// of operation.
export const endOfPlainDateInTimeZone = ({
  date,
  timeZone,
}: {
  date: string;
  timeZone: ITimeZone;
}) => {
  const [year, month, day] = z.iso
    .date()
    .parse(date)
    .split("-")
    .map(Number) as [number, number, number];
  const wallClockAsUtc = Date.UTC(year, month - 1, day, 23, 59, 59, 999);
  const firstEstimate =
    wallClockAsUtc -
    offsetInTimeZone({ instant: new Date(wallClockAsUtc), timeZone });
  return new Date(
    wallClockAsUtc -
      offsetInTimeZone({ instant: new Date(firstEstimate), timeZone }),
  );
};

const timeSpanRegex =
  /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](?:\.[0-9]+)?$/;
// Matches date in YYYY-MM-DD format and validates it's a real date (with some limitations, see below)
// - Year (YYYY): 4 digits (1900-2099)
// - Month (MM): 01-12 (requires leading zero)
// - Day (DD): 01-31 (requires leading zero)
// Examples: "2024-01-01", "2024-12-31"

export const timespanSchema = z.string().regex(timeSpanRegex);
export type ITimespan = z.infer<typeof timespanSchema>;
